import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { makeState } from './state'
import { makeGetters } from './getters'
import { makeSync } from './sync'
import { setUIPluginInstallations as applyUIPluginInstallations } from './installations'

import { makeAuthApi } from './api/auth'
import { makeViewApi } from './api/view'
import { makeWalletsApi } from './api/wallets'
import { makeCoinsApi } from './api/coins'
import { makeSettingsApi } from './api/settings'
import { getAuthToken } from './api/http'

import { makeLiveWs } from './live/ws'

const NO_WALLET_KEY = '__none__'

export const useStorage = defineStore('storage', () => {
  const token = ref(null)
  const state = ref(makeState())
  const pnlPeriod = ref('day')
  const selectedWalletKey = ref(readSelectedWalletKey())

  const sync = makeSync(state)

  const getters = makeGetters(token, state)
  const { isLoggedIn } = getters
  const selectedWalletRows = computed(() => walletRows(state.value.wallets || {}))
  const selectedWallet = computed(() => {
    const rows = selectedWalletRows.value
    if (rows.length === 0) return null
    const current = String(selectedWalletKey.value || '').trim()
    if (current === NO_WALLET_KEY) return null
    if (current) {
      const wallet = rows.find(row => row.public_key === current)
      if (wallet) return wallet
    }
    const legacy = String(state.value.settings?.autotrade?.wallet || '').trim()
    if (legacy) {
      const wallet = rows.find(row => row.public_key === legacy)
      if (wallet) return wallet
    }
    const owner = Object.keys(state.value.settings?.trade?.owners || {}).find(key => rows.some(row => row.public_key === key))
    if (owner) return rows.find(row => row.public_key === owner) || rows[0]
    return rows[0]
  })

  const authApi = makeAuthApi({ token: token })
  const viewApi = makeViewApi()
  const walletsApi = makeWalletsApi()
  const coinsApi = makeCoinsApi()
  const settingsApi = makeSettingsApi({ state })

  const liveWs = makeLiveWs({ token: token, isLoggedIn, sync })

  watch(selectedWallet, wallet => {
    if (!wallet && selectedWalletKey.value === NO_WALLET_KEY) return
    const key = wallet?.public_key || ''
    if (selectedWalletKey.value === key) return
    selectedWalletKey.value = key
    persistSelectedWalletKey(key)
  }, { immediate: true })

  async function login(publicKey, message, signature) {
    await authApi.login(publicKey, message, signature)
    liveWs.live(authApi.check).catch(console.error)
    return true
  }

  async function check() {
    token.value = getAuthToken()
    return await authApi.check()
  }

  async function live() {
    return await liveWs.live(authApi.check)
  }

  async function logout() {
    await liveWs.terminate()
    await authApi.logout()
    state.value = makeState()
  }

  async function terminate() {
    await liveWs.terminate()
  }

  function setUIPluginInstallations(installations) {
    applyUIPluginInstallations(state, installations)
  }

  function setSelectedWalletKey(publicKey) {
    const key = String(publicKey || '').trim()
    const wallet = selectedWalletRows.value.find(row => row.public_key === key)
    if (!wallet) return false
    selectedWalletKey.value = wallet.public_key
    persistSelectedWalletKey(wallet.public_key)
    return true
  }

  function clearSelectedWalletKey() {
    selectedWalletKey.value = NO_WALLET_KEY
    persistSelectedWalletKey(NO_WALLET_KEY)
  }

  // view
  async function view(mint, page = 1) {
    return await viewApi.view(mint, page)
  }

  // wallets
  const { createWallet, deleteWallet, listWallets, restoreWallet, renameWallet, importWallet, transferWallet, walletTrades } = walletsApi

  async function tradeWallet(mint, publicKey, side, pct) {
    const m = String(mint || '').trim()
    const pk = String(publicKey || '').trim()
    const method = String(side || '').trim() // 'buy' | 'sell'
    const p = Math.floor(Number(pct))

    if (!m || !pk) throw new Error('trade_invalid_params')
    if (method !== 'buy' && method !== 'sell') throw new Error('trade_invalid_method')
    if (!Number.isFinite(p) || p < 1 || p > 100) throw new Error('trade_invalid_percent')

    // через новый хелпер:
    return coinsApi.createTradeSingle(m, method, pk, p)

    // или без него:
    // return coinsApi.createTradeBatch(m, method, { [pk]: p })
  }

  async function tradeWalletBatch(mint, side, ownersMap) {
    const m = String(mint || '').trim()
    const method = String(side || '').trim() // 'buy' | 'sell'

    if (!m) throw new Error('trade_invalid_mint')
    if (method !== 'buy' && method !== 'sell') throw new Error('trade_invalid_method')
    if (!ownersMap || typeof ownersMap !== 'object') throw new Error('trade_invalid_owners')

    // ownersMap: { [publicKey]: percent } or { [publicKey]: { pct, ...settings } }
    for (const [pk, value] of Object.entries(ownersMap)) {
      const p = Math.floor(Number(value && typeof value === 'object' ? value.pct : value))
      if (!String(pk || '').trim()) throw new Error('trade_invalid_owner')
      if (!Number.isFinite(p) || p < 1 || p > 100) throw new Error('trade_invalid_percent')
    }

    // через coinsApi:
    return coinsApi.createTradeBatch(m, method, ownersMap)
  }

  // coins
  const {
    createCoin,
    editCoin,
    ownCoin,
    deleteCoin,
    launchCoin,
    createTradeBatch,
    createGuardedTradeBatch,
  } = coinsApi

  // settings
  const { updateFilter, updateMints, updateLaunch, updateTrade, updateAutotrade, updateHolders, updateTrades, updateWallets, updateCoins, updateNft } = settingsApi

  return {
    token: token,
    state,
    pnlPeriod,
    selectedWalletKey,
    selectedWallet,
    setSelectedWalletKey,
    clearSelectedWalletKey,

    ...getters,

    check,
    login,
    logout,
    live,
    terminate,
    setUIPluginInstallations,

    view,

    createWallet,
    renameWallet,
    deleteWallet,
    listWallets,
    restoreWallet,
    importWallet,
    transferWallet,
    walletTrades,
    tradeWallet,
    tradeWalletBatch,

    createCoin,
    editCoin,
    ownCoin,
    deleteCoin,
    launchCoin,

    updateFilter,
    updateMints,
    updateLaunch,
    updateTrade,
    updateAutotrade,
    updateHolders,
    updateTrades,
    updateWallets,
    updateCoins,
    updateNft,

    createTradeBatch,
    createGuardedTradeBatch,
  }
})

function readSelectedWalletKey() {
  try {
    return String(window.localStorage?.getItem('aime.ui.selected-wallet') || '').trim()
  } catch {
    return ''
  }
}

function persistSelectedWalletKey(value) {
  try {
    window.localStorage?.setItem('aime.ui.selected-wallet', String(value || ''))
  } catch {
    return
  }
}

function walletRows(source) {
  if (!source || typeof source !== 'object') return []
  const entries = Array.isArray(source)
    ? source.map(wallet => [walletKey(wallet), wallet])
    : Object.entries(source)
  return entries
    .map(([key, wallet]) => {
      const row = wallet && typeof wallet === 'object' ? wallet : {}
      const publicKey = walletKey(row) || String(key || '').trim()
      return { ...row, public_key: publicKey, publicKey }
    })
    .filter(wallet => walletKey(wallet))
}

function walletKey(wallet) {
  return String(wallet?.public_key || wallet?.publicKey || wallet?.PublicKey || '').trim()
}
